import { cn } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useCompetitionAuth } from "@/modules/auth/presentation/model/useCompetitionAuth";
import { useJudgeEventAccess } from "@/modules/events/presentation/api/queries";

type Props = {
  judgeNumber: 1 | 2 | 3;
  eventId: string;
  children: React.ReactNode;
};

const JUDGE_LABELS: Record<number, string> = {
  1: "Judge 1 — Dance Skill & Technique",
  2: "Judge 2 — Stage Presence & Performance",
  3: "Judge 3 — Concept & Creativity",
};

export function CompetitionJudgeLayout({
  judgeNumber,
  eventId,
  children,
}: Props) {
  const {
    isLoading,
    judgeNumber: currentJudge,
    isJudge,
    isCompetitionUser,
    isCompetitionAdmin,
    signOut,
  } = useCompetitionAuth();
  const navigate = useNavigate();

  const accessQuery = useJudgeEventAccess(
    eventId,
    Boolean(eventId) && isCompetitionUser,
  );

  useEffect(() => {
    if (!isLoading && !isCompetitionUser) {
      navigate({ to: "/competition/login" });
    }
  }, [isLoading, isCompetitionUser, navigate]);

  if (isLoading || (isCompetitionUser && accessQuery.isPending)) {
    return (
      <div className="flex h-svh items-center justify-center bg-background">
        <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const isCorrectJudge = currentJudge === judgeNumber;

  if (!isJudge && !isCompetitionAdmin) {
    return (
      <div className="flex h-svh flex-col items-center justify-center gap-4 bg-background">
        <p className="font-bold text-foreground text-xl">ບໍ່ມີສິດທິ້ເຂົ້າເຖິງ</p>
        <p className="text-muted-foreground text-sm">
          ໜ້ານີ້ສໍາລັບ {JUDGE_LABELS[judgeNumber]} ເທົ່ານັ້ນ
        </p>
        <button
          type="button"
          onClick={async () => {
            await signOut();
            navigate({
              to: "/competition/judge-login/$eventId/$judgeNumber",
              params: { eventId, judgeNumber: String(judgeNumber) },
            });
          }}
          className={cn(
            "rounded-xl bg-secondary px-6 py-2 text-secondary-foreground text-sm",
            "transition-colors hover:bg-secondary/80",
          )}
        >
          ອອກຈາກລະບົບ
        </button>
      </div>
    );
  }

  if (isJudge && !isCorrectJudge) {
    return (
      <div className="flex h-svh flex-col items-center justify-center gap-4 bg-background">
        <p className="font-bold text-foreground text-xl">ບໍ່ມີສິດທິ້ເຂົ້າເຖິງ</p>
        <p className="text-muted-foreground text-sm">
          ໜ້ານີ້ສໍາລັບ {JUDGE_LABELS[judgeNumber]} ເທົ່ານັ້ນ
        </p>
        <button
          type="button"
          onClick={async () => {
            await signOut();
            navigate({
              to: "/competition/judge-login/$eventId/$judgeNumber",
              params: { eventId, judgeNumber: String(judgeNumber) },
            });
          }}
          className={cn(
            "rounded-xl bg-secondary px-6 py-2 text-secondary-foreground text-sm",
            "transition-colors hover:bg-secondary/80",
          )}
        >
          ອອກຈາກລະບົບ
        </button>
      </div>
    );
  }

  if (isJudge && accessQuery.isError) {
    return (
      <div className="flex h-svh flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <p className="font-bold text-foreground text-xl">ບໍ່ມີສິດສຳລັບງານນີ້</p>
        <p className="text-muted-foreground text-sm">
          Session ບໍ່ຖືກຜູກກັບງານນີ້ — ເຂົ້າໃໝ່ດ້ວຍລິ້ງກຳມະການທີ່ຜູ້ຈັດສົ່ງ
        </p>
        <button
          type="button"
          onClick={async () => {
            await signOut();
            navigate({
              to: "/competition/judge-login/$eventId/$judgeNumber",
              params: { eventId, judgeNumber: String(judgeNumber) },
            });
          }}
          className={cn(
            "rounded-xl bg-secondary px-6 py-2 text-secondary-foreground text-sm",
          )}
        >
          ກັບໄປໜ້າເຂົ້າລະບົບ
        </button>
      </div>
    );
  }

  return <div className="flex min-h-dvh flex-col">{children}</div>;
}
